import { logInfo, logWarning, logError } from "../../../services/logging/log-handler.js";

export type WorkingDocument = {
  documentId: string;
  extractedAt: string;
  tables: WorkingTable[];
};

export type WorkingTable = {
  page: number;
  name: string;
  data?: never[]; // legacy not used
  initial?: never; // legacy not used
  work?: TargetTable[];
};

export type ColumnDef = {
  id: string;
  label: string;
  key: string;
  match: boolean | string;
};

export type Cell = {
  id: string;
  value: string;
  source: 'user' | 'system';
};

export type Row = {
  id: string;
  key?: string;
  match: boolean | string;
  cells: Cell[];
};

export type TargetTable = {
  target: string; // e.g., "age", "gender"
  next_row: string;
  next_col: string;
  columns: ColumnDef[];
  rows: Row[];
};

export type ExtractInput = {
  documentId: string;
  extractedAt: string;
  tables: Array<{
    page: number;
    name: string;
    data: Record<string, string[]>;
  }>;
};

export function convertExtractToWorking(extractData: ExtractInput): WorkingDocument {
  const result: WorkingDocument = {
    documentId: extractData.documentId,
    extractedAt: extractData.extractedAt,
    tables: []
  };

  extractData.tables.forEach((table) => {
    const workingTable: WorkingTable = {
      page: table.page,
      name: table.name,
      data: [] as never[]
    };

    const columns = Object.keys(table.data);
    const rowCount = table.data[columns[0]]?.length || 0;

    const columnDefs: ColumnDef[] = columns.map((colName, colIndex) => ({
      id: `c${colIndex + 1}`,
      label: colName,
      key: '',
      match: false
    }));

    const rows: Row[] = [];

    for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
      const row: Row = {
        id: `r${rowIndex + 1}`,
        match: true,
        cells: []
      };

      columns.forEach((colName, colIndex) => {
        const value = table.data[colName][rowIndex] ?? '';
        row.cells.push({
          id: `c${colIndex + 1}`,
          value: String(value),
          source: 'system'
        });
      });

      rows.push(row);
    }

    const tableDataInitial: TargetTable = {
      target: 'initial',
      next_row: `r${rowCount + 1}`,
      next_col: `c${columns.length + 1}`,
      columns: JSON.parse(JSON.stringify(columnDefs)),
      rows: JSON.parse(JSON.stringify(rows))
    };

    const tableDataCurrent: TargetTable = {
      target: 'current',
      next_row: `r${rowCount + 1}`,
      next_col: `c${columns.length + 1}`,
      columns: JSON.parse(JSON.stringify(columnDefs)),
      rows: JSON.parse(JSON.stringify(rows))
    };

    (workingTable as any).data = [tableDataInitial, tableDataCurrent];

    result.tables.push(workingTable);
  });

  return result;
}

export function convertWorkingToExtract(workingData: WorkingDocument): ExtractInput {
  const result: ExtractInput = {
    documentId: (workingData as any).documentId,
    extractedAt: (workingData as any).extractedAt,
    tables: []
  };

  (workingData.tables as any[]).forEach((table: any) => {
    const currentData: TargetTable | undefined = Array.isArray(table.work) ? table.work[0] : undefined;
    if (!currentData) return;

    const extractTable = {
      page: table.page as number,
      name: table.name as string,
      data: {} as Record<string, string[]>
    };

    currentData.columns.forEach((col) => {
      extractTable.data[col.label] = [];
    });

    currentData.rows.forEach((row) => {
      row.cells.forEach((cell) => {
        const column = currentData.columns.find(c => c.id === cell.id);
        if (column) {
          extractTable.data[column.label].push(cell.value);
        }
      });
    });

    result.tables.push(extractTable);
  });

  return result;
}

export function getCurrentVersion(workingData: WorkingDocument, tableIndex = 0, targetId = 'age'): TargetTable | null {
  if (!workingData.tables || !workingData.tables[tableIndex]) {
    return null;
  }

  const table: any = workingData.tables[tableIndex];

  if (Array.isArray(table.work)) {
    return table.work.find((target: any) => target.target === targetId) || null;
  }

  return null;
}

export function updateCellValue(
  workingData: WorkingDocument,
  tableIndex: number,
  rowId: string,
  colId: string,
  newValue: string,
  source: 'user' | 'system' = 'user',
  targetId = 'work'
): void {
  const currentData = getCurrentVersion(workingData, tableIndex, targetId);
  if (!currentData) return;

  const row = currentData.rows.find(r => r.id === rowId);
  if (!row) return;

  const cell = row.cells.find(c => c.id === colId);
  if (!cell) return;

  cell.value = newValue;
  cell.source = source;
}

export function addRow(
  workingData: WorkingDocument,
  tableIndex: number,
  values: string[] = [],
  targetId = 'work'
): void {
  const currentData = getCurrentVersion(workingData, tableIndex, targetId);
  if (!currentData) return;

  const newRowId = currentData.next_row;
  const newRow: Row = {
    id: newRowId,
    match: true,
    cells: []
  };

  currentData.columns.forEach((col, index) => {
    newRow.cells.push({
      id: col.id,
      value: values[index] || '',
      source: 'user'
    });
  });

  currentData.rows.push(newRow);

  const rowNumber = parseInt(newRowId.substring(1)) + 1;
  currentData.next_row = `r${rowNumber}`;
}

export function addColumn(
  workingData: WorkingDocument,
  tableIndex: number,
  label: string,
  key = '',
  targetId = 'work'
): void {
  const currentData = getCurrentVersion(workingData, tableIndex, targetId);
  if (!currentData) return;

  const newColId = currentData.next_col;
  const newColumn: ColumnDef = {
    id: newColId,
    label,
    key,
    match: false
  };

  currentData.columns.push(newColumn);

  currentData.rows.forEach(row => {
    row.cells.push({
      id: newColId,
      value: '',
      source: 'user'
    });
  });

  const colNumber = parseInt(newColId.substring(1)) + 1;
  currentData.next_col = `c${colNumber}`;
}

export function removeRow(
  workingData: WorkingDocument,
  tableIndex: number,
  rowId: string,
  targetId = 'work'
): void {
  const currentData = getCurrentVersion(workingData, tableIndex, targetId);
  if (!currentData) return;

  currentData.rows = currentData.rows.filter(row => row.id !== rowId);
}

export function removeColumn(
  workingData: WorkingDocument,
  tableIndex: number,
  colId: string,
  targetId = 'work'
): void {
  const currentData = getCurrentVersion(workingData, tableIndex, targetId);
  if (!currentData) return;

  currentData.columns = currentData.columns.filter(col => col.id !== colId);
  currentData.rows.forEach(row => {
    row.cells = row.cells.filter(cell => cell.id !== colId);
  });
}

export function renameColumn(
  workingData: WorkingDocument,
  tableIndex: number,
  colId: string,
  newLabel: string,
  newKey = '',
  targetId = 'work'
): void {
  const currentData = getCurrentVersion(workingData, tableIndex, targetId);
  if (!currentData) return;

  const column = currentData.columns.find(col => col.id === colId);
  if (column) {
    column.label = newLabel;
    if (newKey !== undefined) {
      column.key = newKey;
    }
  }
}

export function resetToInitial(
  workingData: WorkingDocument,
  tableIndex: number,
  targetId: string
): void {
  // Get the table
  const table = workingData.tables[tableIndex];
  if (!table) return;

  // Get the initial data from table.data (the preprocessed original data)
  // Note: table.data is typed as never[] but actually contains the preprocessed data structure
  const initialData = (table as any).data;
  if (!initialData || !initialData.columns || !initialData.rows) {
    logWarning('resetToInitial: No initial data found in table.data', "data-conversion-utils", undefined);
    return;
  }

  // Find the work entry for the target
  if (!Array.isArray(table.work)) {
    logWarning('resetToInitial: No work array found', "data-conversion-utils", undefined);
    return;
  }

  const workEntry = table.work.find((w: any) => w.target === targetId);
  if (!workEntry) {
    logWarning(`resetToInitial: No work entry found for target: ${targetId}`, "data-conversion-utils", undefined);
    return;
  }

  // Reset the work entry by copying from initial data
  workEntry.columns = JSON.parse(JSON.stringify(initialData.columns));
  workEntry.rows = JSON.parse(JSON.stringify(initialData.rows));

  // Calculate next_row and next_col based on the reset data
  const rowCount = workEntry.rows.length;
  const colCount = workEntry.columns.length;
  workEntry.next_row = `r${rowCount + 1}`;
  workEntry.next_col = `c${colCount + 1}`;
}


